import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {TeamCompaniesService} from '../../../services/teamcompanies/teamcompanies.service';
import {TeamCompanyUsersService} from '../../../services/teamcompanyusers/teamcompanyusers.service';

export let rememberedAgencyId = '';

@Component({
    selector: 'app-agency',
    templateUrl: './agency.component.html',
    styleUrls: ['./agency.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AgencyComponent implements OnInit, OnDestroy {

  public links = [
    { title: 'Profile', link: '/brokeragency/agency/agency-profile' },
    { title: 'Clients', link: '/brokeragency/agency/agency-clients' },
    { title: 'Members', link: '/brokeragency/agency/agency-members' },
    { title: 'Producers', link: '/brokeragency/agency/agency-producers' }
  ];

  public activeLink: { title: string; link: string } | undefined;
  public selectedAgencyId = '';
  public agencyName = '';
  public agencies: any[] = [];
  public agencyClientCounts: {[agencyId: string]: number} = {};
  public info = [
    {title: 'Clients', value: 0, background: 'lightskyblue'},
    {title: 'Members', value: 0, background: 'orange'},
    {title: 'Producers', value: 0, background: 'yellowgreen'},
  ];

  private subs: Subscription[] = [];
  private pendingRouteAgencyId: string | null = null;
  private agenciesLoaded = false;

  constructor(
      private router: Router,
      private route: ActivatedRoute,
      private teamCompaniesService: TeamCompaniesService,
      private teamCompanyUsersService: TeamCompanyUsersService
  ) { }

  ngOnInit(): void {
    this.selectedAgencyId = rememberedAgencyId;

    this.setActiveLinkFromUrl();
    this.subs.push(
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(() => {
              this.setActiveLinkFromUrl();
            })
    );

    this.subs.push(
        this.route.paramMap.subscribe((pm) => {
          const id = pm.get('id');
          if (id) {
            this.pendingRouteAgencyId = id;
            this.applyAgencyIdFromRouteIfPossible();
          }
        })
    );

    this.teamCompaniesService.getCompanies(this.teamCompanyUsersService.roles.broker ? 'broker' : 'agency')
        .then((companies: any[]) => {
          const allCompanies = companies || [];
          this.agencyClientCounts = this.getAgencyClientCounts(allCompanies);
          this.agencies = allCompanies.filter((c: { type: string; }) => c.type === 'agency');
          this.agenciesLoaded = true;

          if (this.pendingRouteAgencyId) {
            if (this.setAgencyByIdIfValid(this.pendingRouteAgencyId)) {
              return;
            }
          }

          if (this.selectedAgencyId) {
            if (this.setAgencyByIdIfValid(this.selectedAgencyId)) {
              this.ensureUrlHasAgencyId();
              return;
            }
          }

          if (this.agencies.length > 0) {
            this.setAgencyById(this.agencies[0].id);
            this.ensureUrlHasAgencyId();
            return;
          }

          const roleAgencyId = this.teamCompanyUsersService.roles.agency?.id;
          if (roleAgencyId) {
            this.setAgencyById(roleAgencyId);
          }
          this.agencyName = this.teamCompanyUsersService.roles.agency?.name || '';
        });
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
  }

  setCount(fieldName: string, value: number): void {
    const item = this.info.find(i => i.title === fieldName);
    if (item) {
      item.value = value;
      this.info = this.info.slice();
    }
  }

  gotoTab(linkObj: { title: string; link: string }): void {
    this.activeLink = linkObj;
    this.ensureUrlHasAgencyId();
  }

  setAgency(event: any): void {
    this.setAgencyById(event.value);
    this.ensureUrlHasAgencyId();
  }

  setAgencyId(id: any): void {
    console.log('agency changed', id);
    if (!id) {
      return;
    }
    const nextId = String(id);
    if (this.selectedAgencyId === nextId) {
      return;
    }
    this.selectedAgencyId = nextId;
    rememberedAgencyId = nextId;
    this.navigateToActiveTabWithId();
  }

  private getAgencyClientCounts(companies: any[]): {[agencyId: string]: number} {
    return companies.reduce((counts, company: { parentId: string; type: string }) => {
      if (company.type === 'employer' && company.parentId) {
        counts[company.parentId] = (counts[company.parentId] || 0) + 1;
      }
      return counts;
    }, {});
  }

  private navigateToActiveTabWithId(): void {
    const linkObj = this.activeLink || this.links[0];
    if (!linkObj) {
      return;
    }
    const target = `${linkObj.link}/${this.selectedAgencyId}`;
    if (this.router.url.split('?')[0] === target) {
      return;
    }
    this.router.navigateByUrl(target);
  }

  private setAgencyById(id: string): void {
    this.selectedAgencyId = id;
    rememberedAgencyId = id;
  }

  private setAgencyByIdIfValid(id: string): boolean {
    if (!id) {
      return false;
    }
    const exists = this.agencies.some(a => String(a.id) === String(id));
    if (!exists) {
      return false;
    }
    this.setAgencyById(id);
    return true;
  }

  private applyAgencyIdFromRouteIfPossible(): void {
    if (!this.pendingRouteAgencyId) {
      return;
    }
    if (!this.agenciesLoaded) {
      return;
    }
    if (this.setAgencyByIdIfValid(this.pendingRouteAgencyId)) {
      return;
    }
    if (this.agencies.length > 0) {
      this.setAgencyById(this.agencies[0].id);
      this.ensureUrlHasAgencyId();
    }
  }

  private setActiveLinkFromUrl(): void {
    const path = this.router.url.split('?')[0];
    const parts = path.split('/').filter(Boolean);

    // We want to match links like: /brokeragency/agency/agency-profile
    // If URL has an id: /brokeragency/agency/agency-profile/123, drop the id.
    const base = parts.length >= 3 ? `/${parts.slice(0, 3).join('/')}` : `/${parts.join('/')}`;

    const found = this.links.find(l => l.link === base);
    this.activeLink = found || this.links[0];
  }

  private ensureUrlHasAgencyId(): void {
    const linkObj = this.activeLink || this.links[0];

    if (!this.selectedAgencyId) {
      this.router.navigate([linkObj.link]);
      return;
    }

    const path = this.router.url.split('?')[0];
    const parts = path.split('/').filter(Boolean);

    // Expected parts: ['brokeragency','agency','agency-profile'] or ['brokeragency','agency','agency-profile','123']
    const hasIdAlready = parts.length >= 4 && String(parts[3]) === String(this.selectedAgencyId);

    if (hasIdAlready) {
      return;
    }

    this.router.navigate([linkObj.link, this.selectedAgencyId]);
  }
}
